import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import TechnologyStack from "@/components/TechnologyStack";
import SystemShift from "@/components/SystemShift";
import { Button } from "@/components/ui/button";
import { 
  ArrowRight, Layers, Gauge, Search, CheckCircle2,
  Code2, LayoutTemplate, Server, Database, Cloud
} from "lucide-react";
import {
  heroVariant, sectionVariant, staggerContainer, itemVariant,
  slideInLeft, viewportOnce, viewportOnceNear, glowVariant,
  problemSectionVariant, problemStaggerContainer, problemAccentLineVariant,
  problemBulletVariant, problemHeaderVariant,
} from "@/lib/motion";
import { usePageContent } from "@/hooks/usePageContent";

const iconMap: Record<string, any> = { Layers, Gauge, Search, LayoutTemplate, Code2, Server, Database, Cloud };

const fallback = {
  heroLabel: "Website Development",
  heroHeadline: "Websites engineered for performance, scalability, and growth.",
  heroDescription: "We build high-performance websites that convert visitors into customers. Not templates. Custom solutions designed around your growth objectives.",
  heroCta: "Discuss your project",
  problemTitle: "Why most web projects underperform.",
  problems: [
    "Websites launch without conversion or scalability considerations.",
    "Design decisions are disconnected from performance outcomes.",
    "Slow load times kill conversions before users engage.",
    "Technical debt accumulates as the business grows.",
  ],
  resolutionTitle: "What this service solves.",
  resolutions: [
    "Creates a scalable digital foundation built for growth",
    "Aligns engineering decisions with business and growth objectives",
    "Reduces long-term technical debt and performance risk",
    "Integrates conversion, SEO, and analytics from day one",
  ],
  systemsTitle: "How we engineer growth-ready websites.",
  devSystems: [
    { name: "Conversion-First Architecture", icon: "Layers", description: "We design for outcomes, not aesthetics alone.", components: ["User journey optimization", "Conversion funnel engineering", "Performance-first architecture"], outcome: "Websites that turn visitors into customers." },
    { name: "Performance & Scalability Engineering", icon: "Gauge", description: "We build infrastructure that grows with you.", components: ["Core Web Vitals optimization", "CDN & caching strategy", "Load testing & capacity planning"], outcome: "Sub-second load times at any scale." },
    { name: "CMS & Workflow Enablement", icon: "LayoutTemplate", description: "We empower your team to manage content independently.", components: ["Headless CMS integration", "Content workflow automation", "Role-based access control"], outcome: "Marketing teams ship faster, without developer bottlenecks." },
    { name: "SEO-Ready Foundation", icon: "Search", description: "We build visibility into every layer.", components: ["Semantic HTML structure", "Schema markup implementation", "Technical SEO architecture"], outcome: "Organic visibility from the first deployment." },
  ],
  shiftTitle: "The development shift.",
  shiftBeforeTitle: "Before: Template Approach",
  shiftAfterTitle: "After: Engineered Foundation",
  shiftItems: [
    { label: "Architecture", before: "Template-based, one-size-fits-all solutions", after: "Custom architecture designed for your growth trajectory" },
    { label: "Performance", before: "Slow load times, poor Core Web Vitals", after: "Sub-second loads, optimized for every device" },
    { label: "Scalability", before: "Technical debt accumulates as traffic grows", after: "Infrastructure that scales with your ambition" },
    { label: "Conversion", before: "Design decisions disconnected from outcomes", after: "Every element engineered for conversion" },
    { label: "Maintainability", before: "Developer bottlenecks for simple content updates", after: "Marketing-empowered content management" },
  ],
  techLabel: "Technology",
  techTitle: "Technology stack built for performance.",
  techDescription: "We choose technologies based on your needs. Not trends. Every decision optimizes for speed, scalability, and maintainability.",
  techCategories: [
    { title: "Programming Languages", icon: "Code2", items: ["JavaScript", "TypeScript", "Python", "PHP"] },
    { title: "Frontend", icon: "LayoutTemplate", items: ["React", "Next.js", "Vue.js", "Tailwind CSS"] },
    { title: "Backend", icon: "Server", items: ["Node.js", "Django", "Laravel", "Express.js"] },
    { title: "Databases", icon: "Database", items: ["PostgreSQL", "MongoDB", "MySQL", "Redis"] },
    { title: "Cloud & Hosting", icon: "Cloud", items: ["AWS", "Google Cloud", "Vercel", "Cloudflare"] },
  ],
  forYou: [
    "Businesses ready to invest in a growth-focused digital foundation",
    "Teams that need websites built for conversion, not just aesthetics",
    "Organizations scaling beyond template-based solutions",
    "Companies prioritizing long-term performance over quick launches",
  ],
  notForYou: [
    "Projects requiring template-based quick launches",
    "Businesses needing basic brochure websites",
    "Teams without clear growth objectives defined",
    "Organizations prioritizing lowest cost over long-term value",
  ],
  ctaTitle: "Ready to build a website engineered for growth?",
  ctaDescription: "Let's discuss how a performance-first website can accelerate your business.",
  ctaCta: "Discuss your web project",
};

const resolveIcon = (name: string) => iconMap[name] || Layers;
const resolveTechCats = (cats: any[]) => cats.map((c) => ({ ...c, icon: typeof c.icon === "string" ? resolveIcon(c.icon) : c.icon }));

const WebDevelopment = () => {
  const { content } = usePageContent("web-development", fallback);

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* HERO */}
      <section className="relative pt-20 pb-6 md:pt-32 md:pb-12 lg:pt-44 lg:pb-20 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <motion.div className="absolute top-0 right-1/4 w-[800px] h-[800px] rounded-full" variants={glowVariant} initial="hidden" animate="visible" style={{ background: 'radial-gradient(circle, hsl(var(--primary) / 0.06) 0%, transparent 70%)' }} />
          <motion.div className="absolute bottom-0 left-1/4 w-[600px] h-[600px] rounded-full" variants={glowVariant} initial="hidden" animate="visible" transition={{ delay: 0.2 }} style={{ background: 'radial-gradient(circle, hsl(var(--accent) / 0.04) 0%, transparent 70%)' }} />
        </div>
        <div className="container-wide relative z-10">
          <div className="max-w-3xl">
            <motion.div initial="hidden" animate="visible" variants={staggerContainer}>
              <motion.span variants={heroVariant} className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium mb-4 block">{content.heroLabel}</motion.span>
              <motion.h1 variants={heroVariant} className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-foreground mb-6 leading-[1.1]">{content.heroHeadline}</motion.h1>
              <motion.p variants={heroVariant} className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-8 max-w-2xl">{content.heroDescription}</motion.p>
              <motion.div variants={heroVariant} className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" className="group" asChild><Link to="/contact">{content.heroCta}<ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" /></Link></Button>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="py-10 md:py-14 lg:py-20 border-t border-border">
        <div className="container-wide">
          <motion.div className="max-w-3xl" initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-30%" }} variants={problemSectionVariant}>
            <motion.div variants={problemHeaderVariant}>
              <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium mb-4 block">The Problem</span>
              <h2 className="font-display text-2xl md:text-3xl font-semibold text-foreground mb-10">{content.problemTitle}</h2>
            </motion.div>
            <motion.div className="relative" variants={problemStaggerContainer}>
              <motion.div className="absolute left-0 top-0 bottom-0 w-0.5 bg-primary/15" variants={problemAccentLineVariant} style={{ transformOrigin: "top" }} />
              <div className="space-y-5 pl-6">
                {content.problems.map((p: string, i: number) => (
                  <motion.div key={i} variants={problemBulletVariant} className="group relative">
                    <div className="absolute -left-6 top-0 bottom-0 w-0.5 bg-transparent transition-colors duration-200 md:group-hover:bg-primary" />
                    <p className="text-foreground/80 transition-colors duration-200 md:group-hover:text-foreground">{p}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* RESOLUTION */}
      <section className="py-10 md:py-16 lg:py-20 border-t border-border bg-muted/20">
        <div className="container-wide">
          <motion.div className="max-w-3xl" initial="hidden" whileInView="visible" viewport={viewportOnce} variants={staggerContainer}>
            <motion.span variants={sectionVariant} className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium mb-4 block">The Resolution</motion.span>
            <motion.h2 variants={sectionVariant} className="font-display text-2xl md:text-3xl font-semibold text-foreground mb-10">{content.resolutionTitle}</motion.h2>
            <motion.ul className="space-y-4" variants={staggerContainer}>
              {content.resolutions.map((point: string, i: number) => (
                <motion.li key={i} variants={itemVariant} className="flex items-start gap-3 text-foreground">
                  <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" /><span>{point}</span>
                </motion.li>
              ))}
            </motion.ul>
          </motion.div>
        </div>
      </section>

      {/* DEV SYSTEMS */}
      <section className="py-10 md:py-16 lg:py-20 border-t border-border">
        <div className="container-wide">
          <motion.div className="max-w-3xl mb-12" initial="hidden" whileInView="visible" viewport={viewportOnce} variants={sectionVariant}>
            <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium mb-4 block">Service Systems</span>
            <h2 className="font-display text-2xl md:text-3xl font-semibold text-foreground">{content.systemsTitle}</h2>
          </motion.div>
          <motion.div className="grid md:grid-cols-2 gap-x-12 gap-y-10 lg:gap-y-12" initial="hidden" whileInView="visible" viewport={viewportOnceNear} variants={staggerContainer}>
            {content.devSystems.map((system: any) => {
              const IconComponent = typeof system.icon === "string" ? resolveIcon(system.icon) : system.icon;
              return (
                <motion.div key={system.name} variants={itemVariant}>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0"><IconComponent className="h-5 w-5 text-primary" strokeWidth={1.5} /></div>
                    <div>
                      <h3 className="text-lg font-medium text-foreground mb-2">{system.name}</h3>
                      <p className="text-sm text-muted-foreground mb-3">{system.description}</p>
                      <ul className="space-y-1.5 mb-4">
                        {system.components.map((c: string) => (<li key={c} className="text-sm text-muted-foreground flex items-start gap-2"><span className="text-muted-foreground/50 mt-0.5">·</span>{c}</li>))}
                      </ul>
                      <p className="text-sm text-foreground leading-relaxed"><span className="text-primary">→</span> {system.outcome}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      <SystemShift title={content.shiftTitle} beforeTitle={content.shiftBeforeTitle} afterTitle={content.shiftAfterTitle} items={content.shiftItems} />

      <TechnologyStack label={content.techLabel} title={content.techTitle} description={content.techDescription} categories={resolveTechCats(content.techCategories)} />

      {/* WHO THIS IS FOR */}
      <section className="py-10 md:py-16 lg:py-20 border-t border-border">
        <div className="container-wide">
          <motion.div className="max-w-3xl mb-12" initial="hidden" whileInView="visible" viewport={viewportOnce} variants={sectionVariant}>
            <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium mb-4 block">Fit Assessment</span>
            <h2 className="font-display text-2xl md:text-3xl font-semibold text-foreground">Is this the right fit?</h2>
          </motion.div>
          <motion.div className="grid md:grid-cols-2 gap-8 lg:gap-12" initial="hidden" whileInView="visible" viewport={viewportOnceNear} variants={staggerContainer}>
            <motion.div variants={itemVariant}>
              <h3 className="text-lg font-medium text-foreground mb-6 flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-primary" />Who this is for</h3>
              <ul className="space-y-3">
                {content.forYou.map((item: string, i: number) => (<li key={i} className="text-sm text-muted-foreground flex items-start gap-2"><span className="text-primary mt-1">·</span>{item}</li>))}
              </ul>
            </motion.div>
            <motion.div variants={itemVariant}>
              <h3 className="text-lg font-medium text-foreground mb-6 flex items-center gap-2"><span className="h-5 w-5 rounded-full border-2 border-muted-foreground/30 flex items-center justify-center text-xs text-muted-foreground">✕</span>Who this is not for</h3>
              <ul className="space-y-3">
                {content.notForYou.map((item: string, i: number) => (<li key={i} className="text-sm text-muted-foreground flex items-start gap-2"><span className="text-muted-foreground/50 mt-1">·</span>{item}</li>))}
              </ul>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 border-t border-border bg-muted/20">
        <div className="container-wide">
          <motion.div className="max-w-2xl mx-auto text-center" initial="hidden" whileInView="visible" viewport={viewportOnce} variants={sectionVariant}>
            <h2 className="font-display text-2xl md:text-3xl font-semibold text-foreground mb-4">{content.ctaTitle}</h2>
            <p className="text-muted-foreground mb-8">{content.ctaDescription}</p>
            <Button size="lg" className="group" asChild><Link to="/contact">{content.ctaCta}<ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" /></Link></Button>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default WebDevelopment;
